import React, { type ChangeEvent, type ReactElement, useEffect, useMemo, useState } from 'react'
import ContactDialog from 'features/vendor/vendor-profile/components/contact-dialog/ContactDialog'
import DescriptionDialog from 'features/vendor/vendor-profile/components/description-dialog/DescriptionDialog'
import TopicsDialog from 'features/vendor/vendor-profile/components/topics-dialog/TopicsDialog'
import addVendorPhoto from 'assets/AddVendorPhoto.svg'
import { ReactComponent as CompanyIcon } from 'assets/companyIcon.svg'
import { ReactComponent as ContactPerson } from 'assets/contactPerson.svg'
import { TopicType } from 'common/tenders/tenders.types'
import { ReactComponent as DownloadIcon } from 'assets/downloadIcon.svg'
import { ReactComponent as ClearIcon } from 'assets/clearIcon.svg'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setErrorMessage } from 'store/app.slice'
import { CircularProgress, IconButton, type Palette, Typography, useTheme } from '@mui/material'
import cx from 'classnames'
import { type GeneratedFile, GeneratedFileType } from 'common/files/files.types'
import { downloadFile, getFileUrl, uploadVendorProfilePhoto } from 'common/files/files.service'
import { getTopicIcon, getTopicLabel } from 'common/tenders/tenders.functions'
import { Role } from 'common/utils/types'
import { LookupTypes } from 'features/lookup/lookup.types'
import ThemeButton from 'common/ui/theme-button'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import { useNavigate } from 'react-router-dom'
import SimpleTooltip from 'common/ui/simple-tooltip'
import CellWrapper from 'features/vendors/vendor-list-container/components/cellWrapper'

import FileUploader from '../files/file-uploader'
import { VendorAIStatus } from '../vendor.types'
import { getVendorInfo, setAIRefDocuments, setRefDocuments } from '../vendor.service'
import { getLookup } from '../../lookup/lookup.service'
import { getQuestionnaire, getQuestionnaireResults } from '../questionnaires/questionnaires.service'
import { type Questionnaire } from '../questionnaires/questionnaires.types'

import VendorInfoPanel from './components/vendor-info-panel'
import IndustriesDialog from './components/industries-dialog'
import SpecialitiesDialog from './components/specialty-dialog/SpecialitiesDialog'
import Certificates from './components/certificates'
import styles from './VendorProfile.module.scss'

const specialtiesArray = Array.from({ length: 6 })

const getImplementationStatus = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'Nije završena'
    case 'verified':
      return 'Završena'
    default:
      return '-'
  }
}

const MAX_FILES_NUMBER = 3
const handleVendorAIStatus = (
  palette: Palette,
  vendorAIStatus?: VendorAIStatus,
  note?: string,
  vendorId?: string,
  aiReferenceDocuments?: GeneratedFile[],
  questionnaire?: Questionnaire | null,
): ReactElement => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const disableVerification = !aiReferenceDocuments || aiReferenceDocuments.length === 0
  const { userInfo } = useAppSelector((state) => state.auth)
  const isAdmin = userInfo?.role === Role.admin

  switch (vendorAIStatus) {
    case VendorAIStatus.pending:
      return (
        <div className={styles.rightHeaderContainer}>
          {!isAdmin && (
            <ThemeButton
              variant={BUTTON_VARIANTS.secondary}
              size={BUTTON_SIZE.small}
              onClick={() => {
                if (vendorId && questionnaire) {
                  void dispatch(
                    getQuestionnaireResults({ companyId: vendorId, questionnaireId: questionnaire.id }),
                  )
                  navigate('/profil-kompanije/upitnici/upitnik/vendor-ai-zrelost/rezultati')
                }
              }}
            >
              Rezultati upitnika
            </ThemeButton>
          )}
          <Typography variant="title5" style={{ color: palette.charts.orange400.main }}>
            Čekanje AI verifikacije
          </Typography>
        </div>
      )
    case VendorAIStatus.approved:
      return (
        <div className={styles.rightHeaderContainer}>
          {!isAdmin && (
            <ThemeButton
              variant={BUTTON_VARIANTS.secondary}
              size={BUTTON_SIZE.small}
              onClick={() => {
                if (vendorId && questionnaire) {
                  void dispatch(
                    getQuestionnaireResults({ companyId: vendorId, questionnaireId: questionnaire.id }),
                  )
                  navigate('/profil-kompanije/upitnici/upitnik/vendor-ai-zrelost/rezultati')
                }
              }}
            >
              Rezultati upitnika
            </ThemeButton>
          )}
          <Typography variant="title5" style={{ color: palette.charts.green200.main }}>
            AI verifikovan
          </Typography>
        </div>
      )
    case VendorAIStatus.rejected:
      return (
        <div className={styles.rejectContainer}>
          <Typography variant="title5" style={{ color: palette.charts.red.main }}>
            Zahtev za verifikaciju je odbijen
          </Typography>
          <SimpleTooltip title={note ?? ''} className={styles.tooltip}>
            <Typography variant="subtitle5" className={styles.reject}>
              Razlog: {`${note ?? ''}`}
            </Typography>
          </SimpleTooltip>
        </div>
      )
    default:
      return isAdmin ? (
        <></>
      ) : (
        <SimpleTooltip
          title={
            disableVerification
              ? 'Potrebno je da dodate barem jedan dokument, kako bi mogli da zatražite ai verifikaciju.'
              : ''
          }
        >
          <ThemeButton
            variant={BUTTON_VARIANTS.secondary}
            size={BUTTON_SIZE.large}
            onClick={() => {
              navigate('/profil-kompanije/upitnici/upitnik/vendor-ai-zrelost', { replace: true })
              void dispatch(getQuestionnaire())
            }}
            disabled={disableVerification}
          >
            Zatrazi AI verifikaciju
          </ThemeButton>
        </SimpleTooltip>
      )
  }
}

const getTopicStyle = (topic: TopicType): string => {
  switch (topic) {
    case TopicType.SERVICE:
      return styles.services
    case TopicType.SOFTWARE:
      return styles.software
    case TopicType.HARDWARE:
      return styles.hardware
    default:
      return ''
  }
}

const VendorProfile: React.FC = (): ReactElement => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { palette } = useTheme()

  const { vendor } = useAppSelector((state) => state.vendor)
  const { userInfo } = useAppSelector((state) => state.auth)
  const { lookup } = useAppSelector((state) => state.lookup)
  const { questionnaire } = useAppSelector((state) => state.questionnaires)

  const [loading, setLoading] = useState<number | null>(null)
  const [fileUrl, setFileUrl] = useState<string | undefined>(vendor?.logo?.read?.url)
  const [photoLoading, setPhotoLoading] = useState<boolean>(false)
  const [adjustMode, setAdjustMode] = useState<'height' | 'width' | null>(null)
  const [description, setDescription] = useState(false)
  const [contact, setContact] = useState(false)
  const [topics, setTopics] = useState(false)
  const [industries, setIndustries] = useState<boolean>(false)
  const [specialties, setSpecialties] = useState<boolean>(false)

  const creationDate = vendor?.creationDate ? new Date(vendor.creationDate) : undefined

  const vendorInfo = {
    'Ime kompanije': vendor?.fullName ?? 'Nije pronađeno',
    'Skraćeno ime kompanije': vendor?.name ?? 'Nije pronađeno',
    'Datum osnivanja': creationDate ? creationDate.toLocaleDateString() : 'Nije pronađeno',
    PIB: vendor?.tin ?? 'Nije pronađeno',
    'Delatnost kompanije': vendor?.industry
      ? `${vendor.industry.name} (${vendor.industry.code})`
      : 'Nije pronađeno',
    'Matični broj': vendor?.cid ?? 'Nije pronađeno',
    'Adresa kompanije': vendor?.address ?? 'Nije pronađeno',
    'E-mail': vendor?.aprEmail ?? 'Nije pronađeno',
    'Broj telefona kompanije': vendor?.phones?.length
      ? vendor?.phones.map((phone) => phone + '\n')
      : 'Nije pronađeno',
  }

  useEffect(() => {
    if (userInfo && userInfo.role !== Role.admin) {
      void dispatch(getVendorInfo())
    }
  }, [])

  useEffect(() => {
    if (
      vendor &&
      (vendor.aiStatus === VendorAIStatus.approved || vendor.aiStatus === VendorAIStatus.pending)
    ) {
      void dispatch(getQuestionnaire())
    }
  }, [vendor])

  const handleFileChange = (files: GeneratedFile[]): void => {
    const existingDocuments = vendor?.referenceDocuments?.documents ?? []
    const combinedDocuments = [...existingDocuments, ...files]
    void dispatch(setRefDocuments({ documents: combinedDocuments }))
  }

  const handleDeleteRefDoc = (file: GeneratedFile, index: number): void => {
    const existingDocuments = [...(vendor?.referenceDocuments?.documents ?? [])]
    existingDocuments.splice(index, 1)
    void dispatch(setRefDocuments({ documents: existingDocuments }))
  }

  const handleAIFileChange = (files: GeneratedFile[]): void => {
    const existingDocuments = vendor?.aiReferenceDocuments?.documents ?? []
    const combinedDocuments = [...existingDocuments, ...files]
    void dispatch(setAIRefDocuments({ documents: combinedDocuments }))
  }

  const handleDeleteAIRefDoc = (file: GeneratedFile, index: number): void => {
    const existingDocuments = [...(vendor?.aiReferenceDocuments?.documents ?? [])]
    existingDocuments.splice(index, 1)
    void dispatch(setAIRefDocuments({ documents: existingDocuments }))
  }

  const handleDownloadRefDoc = async (
    file: GeneratedFile,
    index: number,
    type: GeneratedFileType,
  ): Promise<void> => {
    setLoading(index)
    if (file.key) {
      try {
        const generatedFile = await getFileUrl({
          key: file.key,
          mode: 'r',
        })
        await downloadFile(type, generatedFile)
      } catch (error) {
        console.error('Error while handling download:', error)
      } finally {
        setLoading(null)
      }
    }
  }

  const handleLogoUpload = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setPhotoLoading(true)
    const uploadedFile = event.target.files?.[0] as File
    if (vendor?.id && uploadedFile) {
      const generatedFile = await uploadVendorProfilePhoto(uploadedFile, vendor?.id)

      if (generatedFile) {
        setFileUrl(generatedFile.read?.url)
        setTimeout(() => {
          setPhotoLoading(false)
        }, 5000)
      } else {
        dispatch(setErrorMessage('Error uploading photo'))
        setPhotoLoading(false)
      }
    } else {
      setPhotoLoading(false)
    }
  }

  useEffect(() => {
    if (!lookup[LookupTypes.speciality]) {
      void dispatch(getLookup({ value: LookupTypes.speciality }))
    }
    if (!lookup[LookupTypes.industry]) {
      void dispatch(getLookup({ value: LookupTypes.industry }))
    }
  }, [])

  const isAdmin = useMemo<boolean>(() => userInfo?.role === Role.admin ?? false, [userInfo?.role])

  const sumTenderPrice = useMemo(
    () => vendor?.awardedTenders?.reduce((sum, project) => sum + project.price, 0),
    [vendor],
  )

  return vendor ? (
    <div className={styles.container}>
      <div className={styles.vendorInfoContainer}>
        <div className={styles.sidebar}>
          <div className={styles.logo}>
            <IconButton disableRipple component="label">
              {photoLoading ? (
                <CircularProgress />
              ) : (
                <img
                  key={fileUrl}
                  className={cx(styles.photoContent, {
                    [styles.noPointer]: userInfo?.role === Role.admin,
                  })}
                  src={fileUrl ?? addVendorPhoto}
                  height={adjustMode === 'height' ? '100%' : undefined}
                  width={adjustMode === 'width' ? '100%' : undefined}
                  onLoad={(event) => {
                    const { naturalWidth, naturalHeight } = event.target as HTMLImageElement
                    if (naturalHeight > naturalWidth) {
                      setAdjustMode('width')
                    } else if (naturalWidth > naturalHeight) {
                      setAdjustMode('height')
                    }
                  }}
                />
              )}
              <input
                type="file"
                disabled={userInfo?.role === Role.admin}
                accept="image/*"
                hidden
                onChange={(e) => void handleLogoUpload(e)}
              />
            </IconButton>
          </div>
          <div className={styles.info}>
            {Object.entries(vendorInfo).map(([key, value]) => (
              <div key={key} className={styles.infoItem}>
                <Typography variant="title3">{key}</Typography>
                <Typography variant="subtitle1" sx={{ whiteSpace: 'pre-wrap' }} className={styles.infoValue}>
                  {value}
                </Typography>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.panelGroup}>
            <VendorInfoPanel
              title="Opis kompanije"
              className={styles.descriptionPanel}
              icon={<CompanyIcon fill={theme.palette.green.main} />}
              style={{ minHeight: '284px', width: '70%' }}
              empty={isAdmin ? undefined : !!vendor.description}
              onClick={() => {
                setDescription(true)
              }}
            >
              <Typography variant="subtitle1">
                {vendor.description ??
                  (isAdmin ? 'Korisnik nije dodao opis svoje kompanije.' : 'Dodajte opis svoje kompanije.')}
              </Typography>
            </VendorInfoPanel>
            <VendorInfoPanel
              title="Kontakt osoba"
              icon={<ContactPerson fill={theme.palette.green.main} />}
              className={styles.contactPanel}
              contentStyle={{ padding: '8px', width: '100%' }}
              empty={isAdmin ? undefined : !!vendor.contact}
              onClick={() => {
                setContact(true)
              }}
            >
              <div className={styles.contact}>
                <Typography
                  variant="title3"
                  style={{ textAlign: isAdmin && !vendor.contact ? 'center' : undefined, lineHeight: '27px' }}
                >
                  {vendor.contact ? (
                    <>
                      {`${vendor.contact.name?.first ?? ''} ${vendor.contact.name?.last ?? ''}`} <br />
                      {vendor.contact.role} <br />
                      {vendor.contact.phone} <br />
                      {vendor.contact.email} <br />
                    </>
                  ) : isAdmin ? (
                    'Nije dodata kontakt osoba od strane korisnika.'
                  ) : (
                    'Dodajte kontakt osobu koja će biti zadužena za komunikaciju za tendere. Ovaj podatak je obavezan za učestvovanje na tenderima.'
                  )}
                </Typography>
              </div>
            </VendorInfoPanel>
          </div>
          <div className={styles.panelGroup}>
            <VendorInfoPanel
              title="Oblasti rada"
              icon={<CompanyIcon fill={theme.palette.green.main} />}
              style={{ flexGrow: '1', height: '266px', minWidth: '200px' }}
              contentStyle={{ padding: '24px 16px' }}
              empty={isAdmin ? undefined : !!vendor.topics}
              onClick={() => {
                setTopics(true)
              }}
            >
              {vendor.topics ? (
                <div className={styles.topics}>
                  {[TopicType.SERVICE, TopicType.SOFTWARE, TopicType.HARDWARE]
                    .filter((topic) => {
                      return !(vendor.isConsultant && topic === TopicType.HARDWARE)
                    })
                    .map((topic) => (
                      <Typography
                        key={topic}
                        variant="title1"
                        className={cx(styles.topic, getTopicStyle(topic), {
                          [styles.deselected]: !vendor.topics?.includes(topic),
                        })}
                      >
                        {getTopicIcon(topic)}
                        {getTopicLabel(topic)}
                      </Typography>
                    ))}
                </div>
              ) : (
                <Typography variant="subtitle1">
                  {isAdmin
                    ? 'Oblasti rada nisu dodate od strane korisnika.'
                    : 'Dodajte Vaše oblasti rada kako bi mogli da učestvujete na tenderima.'}
                </Typography>
              )}
            </VendorInfoPanel>
            <VendorInfoPanel
              title="Spremnost za AI"
              className={styles.referentDocumentsPanel}
              contentClassName={styles.documents}
              rightHeader={handleVendorAIStatus(
                palette,
                vendor.aiStatus,
                vendor.aiVerificationNote,
                vendor.id,
                vendor?.aiReferenceDocuments?.documents,
                questionnaire,
              )}
              rightHeaderClassName={styles.rightHeader}
            >
              {vendor.aiReferenceDocuments?.documents && vendor.aiReferenceDocuments?.documents.length > 0 ? (
                <>
                  {vendor.aiReferenceDocuments.documents.map((document, index) => (
                    <div key={index} className={styles.document}>
                      <div className={styles.iconContainer}>
                        {loading === index ? (
                          <div className={styles.loader}>
                            <CircularProgress size={15} />
                          </div>
                        ) : (
                          <DownloadIcon
                            onClick={() => {
                              void handleDownloadRefDoc(document, index, GeneratedFileType.AIVendorRefDoc)
                            }}
                            height="16px"
                            width="16px"
                            fill={theme.palette.blue100.main}
                          />
                        )}
                      </div>
                      <div className={styles.documentContent}>
                        <Typography variant="subtitle1" className={styles.truncate}>
                          {document.filename}
                        </Typography>
                        {!isAdmin && (
                          <ClearIcon
                            onClick={() => {
                              handleDeleteAIRefDoc(document, index)
                            }}
                            stroke={theme.palette.white.main}
                            height="16px"
                            width="16px"
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : isAdmin ? (
                <Typography variant="subtitle1">Dokumenti nisu dodati od strane korisnika.</Typography>
              ) : (
                <></>
              )}
              {((vendor.aiReferenceDocuments &&
                vendor.aiReferenceDocuments.documents.length < MAX_FILES_NUMBER) ??
                !vendor.aiReferenceDocuments) &&
              !isAdmin ? (
                <FileUploader
                  onFileChange={handleAIFileChange}
                  multiple={false}
                  fileType={GeneratedFileType.AIVendorRefDoc}
                  placeholder="Podržan je isključivo PDF format"
                  accept=".pdf"
                />
              ) : (
                <></>
              )}
            </VendorInfoPanel>
            {vendor?.user?.role !== Role.consultant ? (
              <VendorInfoPanel
                title="Referentna dokumenta"
                icon={<CompanyIcon fill={theme.palette.green.main} />}
                className={styles.referentDocumentsPanel}
                contentClassName={styles.documents}
              >
                {vendor.referenceDocuments?.documents && vendor.referenceDocuments?.documents.length > 0 ? (
                  <>
                    {vendor.referenceDocuments.documents.map((document, index) => (
                      <div key={index} className={styles.document}>
                        <div className={styles.iconContainer}>
                          {loading === index ? (
                            <div className={styles.loader}>
                              <CircularProgress size={15} />
                            </div>
                          ) : (
                            <DownloadIcon
                              onClick={() => {
                                void handleDownloadRefDoc(document, index, GeneratedFileType.VendorRefDoc)
                              }}
                              height="16px"
                              width="16px"
                              fill={theme.palette.blue100.main}
                            />
                          )}
                        </div>
                        <div className={styles.documentContent}>
                          <Typography variant="subtitle1" className={styles.truncate}>
                            {document.filename}
                          </Typography>
                          {!isAdmin && (
                            <ClearIcon
                              onClick={() => {
                                handleDeleteRefDoc(document, index)
                              }}
                              stroke={theme.palette.white.main}
                              height="16px"
                              width="16px"
                              style={{ cursor: 'pointer' }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                ) : isAdmin ? (
                  <Typography variant="subtitle1">Dokumenti nisu dodati od strane korisnika.</Typography>
                ) : (
                  <></>
                )}
                {((vendor.referenceDocuments && vendor.referenceDocuments.documents.length < 3) ??
                  !vendor.referenceDocuments) &&
                !isAdmin ? (
                  <FileUploader
                    onFileChange={handleFileChange}
                    multiple={false}
                    fileType={GeneratedFileType.VendorRefDoc}
                    placeholder="Podržan je isključivo PDF format"
                    accept=".pdf"
                  />
                ) : (
                  <></>
                )}
              </VendorInfoPanel>
            ) : (
              <Certificates />
            )}
          </div>
          <VendorInfoPanel
            title="Industrije"
            icon={<CompanyIcon fill={theme.palette.green.main} />}
            style={{ minHeight: '284px' }}
            contentStyle={{ padding: '24px 20px' }}
            empty={isAdmin ? undefined : !!vendor.industries?.length}
            onClick={() => {
              setIndustries(true)
            }}
          >
            {vendor.industries && vendor.industries.length > 0 ? (
              <div className={styles.specialties}>
                {specialtiesArray.map((_, index) => (
                  <div
                    key={index}
                    className={cx(styles.specialty, {
                      [styles.deselected]: vendor.industries && vendor.industries.length <= index,
                    })}
                  >
                    <Typography variant="title1" className={styles.index}>
                      {index + 1}
                    </Typography>
                    <div className={styles.truncateContainer}>
                      <Typography variant="subtitle1" className={styles.truncateSpecialities}>
                        {vendor.industries?.[index]?.name ?? ''}
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            ) : isAdmin ? (
              <Typography variant="subtitle1">Nijedna industrija nije dodata od strane korisnika.</Typography>
            ) : (
              <Typography variant="subtitle1">Dodajte industrije u kojima imate najviše iskustva.</Typography>
            )}
          </VendorInfoPanel>
          <VendorInfoPanel
            title="Specijalnosti"
            icon={<CompanyIcon fill={theme.palette.green.main} />}
            style={{ minHeight: '284px' }}
            contentStyle={{ padding: '24px 20px' }}
            empty={isAdmin ? undefined : !!vendor.specialties?.length}
            onClick={() => {
              setSpecialties(true)
            }}
          >
            {vendor.specialties && vendor.specialties.length > 0 ? (
              <div className={styles.specialties}>
                {specialtiesArray.map((_, index) => (
                  <div
                    key={index}
                    className={cx(styles.specialty, {
                      [styles.deselected]: vendor.specialties && vendor.specialties.length <= index,
                    })}
                  >
                    <Typography variant="title1" className={styles.index}>
                      {index + 1}
                    </Typography>
                    <div className={styles.truncateContainer}>
                      <Typography variant="subtitle1" className={styles.truncateSpecialities}>
                        {vendor.specialties?.[index]?.name ?? ''}
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            ) : isAdmin ? (
              <Typography variant="subtitle1">
                Nijedna specijalnost nije dodata od strane korisnika.
              </Typography>
            ) : (
              <Typography variant="subtitle1">
                Dodajte specijalnosti u kojima imate najviše iskustva.
              </Typography>
            )}
          </VendorInfoPanel>
          <div className={styles.panelGroup}>
            <VendorInfoPanel
              title="Broj prihvaċenih RFB-ova"
              className={styles.contactPanel}
              contentStyle={{ padding: '8px', width: '100%', height: '150px' }}
              style={{ flex: 1, minHeight: '200px' }}
            >
              <div className={styles.contact} style={{ justifyContent: 'center' }}>
                <Typography variant="title11">{vendor.numOfTendersAnsweredRfb ?? 0}</Typography>
              </div>
            </VendorInfoPanel>
            <VendorInfoPanel
              title="Broj poslatih ponuda"
              className={styles.contactPanel}
              contentStyle={{ padding: '8px', width: '100%', height: '150px' }}
              style={{ flex: 1, minHeight: '200px' }}
            >
              <div className={styles.contact} style={{ justifyContent: 'center' }}>
                <Typography variant="title11">{vendor.numOfSubmittedTenders ?? 0}</Typography>
              </div>
            </VendorInfoPanel>
            <VendorInfoPanel
              title="Ukupna dosadašnja zarada"
              className={styles.contactPanel}
              contentStyle={{ padding: '8px', width: '100%', height: '150px' }}
              style={{ flex: 1, minHeight: '200px' }}
            >
              <div className={styles.contact} style={{ justifyContent: 'center' }}>
                <Typography variant="title9">{sumTenderPrice ?? 0} RSD</Typography>
              </div>
            </VendorInfoPanel>
          </div>
        </div>
        {description && <DescriptionDialog isOpen={description} setIsOpen={setDescription} />}
        {contact && <ContactDialog isOpen={contact} setIsOpen={setContact} />}
        {topics && <TopicsDialog isOpen={topics} setIsOpen={setTopics} />}
        {industries && <IndustriesDialog isOpen={industries} setIsOpen={setIndustries} />}
        {specialties && <SpecialitiesDialog isOpen={specialties} setIsOpen={setSpecialties} />}
      </div>
      <div className={styles.awardedTendersContainer}>
        <VendorInfoPanel
          title="Dobijeni tenderi"
          style={{ minHeight: '284px' }}
          contentStyle={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          {vendor.awardedTenders && vendor.awardedTenders.length > 0 ? (
            vendor.awardedTenders?.map((tender, index) => {
              return (
                <div key={index} className={styles.vendorItem}>
                  <CellWrapper
                    title="Naziv programa"
                    value={tender.programName}
                    width="30%"
                    className={styles.programName}
                  />
                  <div className={styles.divider} />
                  <CellWrapper
                    title="Implementirano rešenje"
                    width="20%"
                    value={tender.solution.name}
                    className={styles.implementedSolutionCell}
                  />
                  <div className={styles.divider} />
                  <CellWrapper
                    title="Cena"
                    value={`${tender.price} RSD`}
                    width="10%"
                    style={{ minWidth: '150px', maxWidth: '150px' }}
                  />
                  <div className={styles.divider} />
                  <CellWrapper
                    title="Kompanija"
                    width="15%"
                    value={tender.companyName}
                    style={{ minWidth: '90px', maxWidth: '90px' }}
                  />
                  <div className={styles.divider} />
                  <CellWrapper
                    title="Status implementacije"
                    value={getImplementationStatus(tender.status)}
                    width="13%"
                  />
                </div>
              )
            })
          ) : (
            <div>Trenutno nemate nijedan dobijen tender</div>
          )}
        </VendorInfoPanel>
      </div>
    </div>
  ) : (
    <CircularProgress />
  )
}

export default VendorProfile
